import React, { FC, Suspense } from 'react';
import { filter, head, orderBy, concat, some } from 'lodash';
import { WorkPackage, MaterialOnSite, MaterialTrackingCode, CrewMemberAssignment } from '@bechtel/types';
import {
  Box,
  Text,
  ProgressBarSize,
  Stat,
  ProgressBarDetail,
  ProgressBarDetailSpacing,
  SetSpacing,
} from '@bechtel/gds';
import { ProgressBarContainerStyled, StatsContainerStyled, StatsBlockStyled, StatsListStyled } from './styled';
import { useSideSheetState } from '@bechtel/gds';
import { useBlockPermission, SuspenseLoader, bankersRound } from '@bechtel/client-common';
import { useWorkPackagePermissions } from '~/hooks/use-work-package-permissions';
import { ResolveBlock } from '@bechtel/client-blocks';
import { t } from '@lingui/macro';

interface StatListProps {
  numBlocks: number;
  hoursEarned: number;
  totalEarnableHours: number;
  materialOnsite?: MaterialOnSite;
  workPackage: WorkPackage;
}

const StatList: FC<StatListProps> = ({ numBlocks, hoursEarned, totalEarnableHours, materialOnsite, workPackage }) => {
  const { visible, open, close } = useSideSheetState(false);
  const assignedCrewMembers = concat(workPackage.primaryOwners, workPackage.secondaryOwners, workPackage.otherOwners);
  const { canResolveBlock } = useBlockPermission(
    assignedCrewMembers,
    workPackage.facilities ? workPackage.facilities : []
  );
  return (
    <StatsListStyled>
      <Box flexBasis={{ sm: materialOnsite ? '33%' : '50%' }} justifyContent={{ sm: 'center' }} py={{ sm: '300' }}>
        <Stat
          label={t`Hours Earned vs Total Hours`}
          value={`${bankersRound(hoursEarned, 0)}/${bankersRound(totalEarnableHours, 0)}`}
          spacing={SetSpacing.MEDIUM}
        />
      </Box>
      {materialOnsite && (
        <Box flexBasis={{ sm: '33%' }} justifyContent={{ sm: 'center' }} py={{ sm: '300' }}>
          <Stat
            label={`${materialOnsite.commodityType.name}${t`(s) on Site`}`}
            // TODO: implement numOnSite value once DSC data is available
            // value={`${materialOnsite.numOnSite}/${materialOnsite.totalCommodities}`}
            value={`--/${materialOnsite.totalCommodities}`}
            spacing={SetSpacing.MEDIUM}
          />
        </Box>
      )}
      {numBlocks > 0 ? (
        <>
          <Box
            style={{ cursor: canResolveBlock ? 'pointer' : 'auto' }}
            flexBasis={{ sm: materialOnsite ? '33%' : '50%' }}
            justifyContent={{ sm: 'center' }}
            onClick={() => {
              if (canResolveBlock) open();
            }}
          >
            <Stat label={t`Blocks`} value={numBlocks} spacing={SetSpacing.MEDIUM} />
          </Box>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            {visible && (
              <ResolveBlock
                workPackageId={workPackage.id}
                workPackageTag={workPackage.tag}
                facilities={workPackage.facilities}
                onClose={close}
                assignedCrewMembers={assignedCrewMembers}
              />
            )}
          </Suspense>
        </>
      ) : (
        <Box flexBasis={{ sm: materialOnsite ? '33%' : '50%' }} justifyContent={{ sm: 'center' }} py={{ sm: '300' }}>
          <Stat label={t`Blocks`} value={numBlocks} spacing={SetSpacing.COMPACT} />
        </Box>
      )}
    </StatsListStyled>
  );
};

interface WorkPackageStatsProps {
  workPackage: WorkPackage;
}

const getMaterialsOnsite = (materialOnsiteList: MaterialOnSite[] | null | undefined) => {
  // NOTE: we only want to use MaterialTrackingCode.MATERIAL_COMMODITIES since those contain the onsite vs. offsite numbers
  const filteredList = filter(materialOnsiteList, {
    materialTracking: MaterialTrackingCode.MATERIAL_COMMODITIES,
  });

  // TODO: see if we can get backend to send this list pre-ordered
  const orderedList = orderBy(filteredList, ['totalCommodities'], ['desc']);

  return head(orderedList);
};

const isAnyOwnerAssigned = (crewMemberAssignments: CrewMemberAssignment[]): boolean => {
  return some(crewMemberAssignments, (assignment) => Boolean(assignment.crewMember));
};

export const WorkPackageStats: FC<WorkPackageStatsProps> = ({ workPackage }) => {
  const hasBlocks = workPackage.numBlocks > 0;
  const materialOnsite = getMaterialsOnsite(workPackage.materialsOnSite);
  const isSecondaryOwnersAssigned = isAnyOwnerAssigned([...workPackage.secondaryOwners, ...workPackage.otherOwners]);
  const {
    canEditForeman,
    canEditGeneralForeman,
    canEditSuperintendent,
    canEditFieldEngineer,
  } = useWorkPackagePermissions([
    ...workPackage.primaryOwners,
    ...workPackage.secondaryOwners,
    ...workPackage.otherOwners,
  ]);
  const canEditOwnership = canEditForeman || canEditGeneralForeman || canEditSuperintendent || canEditFieldEngineer;
  const percentageComplete = Math.round(workPackage.percentageComplete ?? 0);
  //TODO: change to function component once changes done in gds for props
  const progressBarLeftLabel: FC = () => {
    return (
      <Text size="300" color="grey.600">
        {hasBlocks ? `${workPackage.numBlocks} ${t`Blocked Milestones`}` : t`In Progress`}&nbsp;-&nbsp;
        <Text size="300" color="grey.600" weight="600">{`${percentageComplete}% ${t`Complete`}`}</Text>
      </Text>
    );
  };

  return (
    <StatsContainerStyled>
      <ProgressBarContainerStyled
        isSecondaryOwnersAssigned={isSecondaryOwnersAssigned}
        canEditOwnership={canEditOwnership}
      >
        <ProgressBarDetail
          size={ProgressBarSize.MEDIUM}
          currentValue={workPackage.earnedHours}
          totalValue={workPackage.totalEarnableHours}
          usePercentage={true}
          percentage={percentageComplete}
          fillPattern={hasBlocks}
          spacing={ProgressBarDetailSpacing.MEDIUM}
          leftLabel={progressBarLeftLabel}
          data-testid="wp-progress-bar"
        />
      </ProgressBarContainerStyled>

      <StatsBlockStyled>
        <StatList
          numBlocks={workPackage.numBlocks}
          hoursEarned={workPackage.earnedHours}
          materialOnsite={materialOnsite}
          workPackage={workPackage}
          totalEarnableHours={workPackage.totalEarnableHours || 0}
        />
      </StatsBlockStyled>
    </StatsContainerStyled>
  );
};
